import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { abbreviated } from "common/numbers";
import Button from "components/common/Button";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

const layerLabel = (layer, index) => {
  const ref = layer?.reference ? ` (${layer.reference})` : "";
  const limit = abbreviated(layer?.limit);
  const attachment = abbreviated(layer?.attachment);
  return `Layer ${index + 1}: ${limit ?? "???"} xs ${
    attachment ?? "???"
  }${ref}`;
};

const PolicyDialog = ({
  open,
  onClose,
  layers,
  initialPolicy,
  existingPolicies,
  onSave,
}) => {
  // Snapshot of the policy being edited at the moment the dialog opened. Held
  // so the dialog keeps showing edit mode (and the right initial values) while
  // it animates closed, even after the parent's editingId has been cleared.
  const [snapshot, setSnapshot] = useState(initialPolicy);
  const [name, setName] = useState(initialPolicy?.name ?? "");
  const [layerIds, setLayerIds] = useState(initialPolicy?.layers ?? []);

  useEffect(() => {
    if (open) {
      setSnapshot(initialPolicy);
      setName(initialPolicy?.name ?? "");
      setLayerIds(initialPolicy?.layers ?? []);
    }
    // Re-sync only when the dialog opens with a different policy id; the
    // initialPolicy reference may churn on parent re-renders while the user
    // is typing, and we don't want those to reset their input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPolicy?.id]);

  const isEdit = Boolean(snapshot);

  const toggleLayer = (id) => {
    setLayerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const trimmedName = name.trim();
  const isDuplicateName = (existingPolicies ?? []).some(
    (p) =>
      p.id !== snapshot?.id &&
      (p.name ?? "").trim().toLowerCase() === trimmedName.toLowerCase() &&
      trimmedName.length > 0
  );
  const canSave =
    trimmedName.length > 0 && layerIds.length > 0 && !isDuplicateName;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>{isEdit ? "Edit policy" : "Create policy"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={"Policy name"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
            error={isDuplicateName}
            helperText={
              isDuplicateName ? "A policy with this name already exists" : " "
            }
          />
          <Typography variant={"subtitle2"}>
            {"Layers in this policy"}
          </Typography>
          <Stack>
            {layers.map((layer, index) => (
              <FormControlLabel
                key={layer.id}
                control={
                  <Checkbox
                    checked={layerIds.includes(layer.id)}
                    onChange={() => toggleLayer(layer.id)}
                  />
                }
                label={layerLabel(layer, index)}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color={"secondary"}>
          {"Cancel"}
        </Button>
        <Button
          color={"primary"}
          isDisabled={!canSave}
          onClick={() => {
            onSave({ name: name.trim(), layers: layerIds });
            onClose();
          }}
        >
          {isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PolicyManager = () => {
  const dispatch = useDispatch();
  const policies = useSelector(pricingSelectors.getPolicies);
  const activePolicyId = useSelector(pricingSelectors.getActivePolicyId);
  const layers = useSelector(pricingSelectors.getTower);

  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const editingPolicy = policies.find((p) => p.id === editingId) ?? null;

  return (
    <>
      <Stack
        direction={"row"}
        spacing={1}
        alignItems={"center"}
        flexWrap={"wrap"}
      >
        {policies.map((p) => (
          <Chip
            key={p.id}
            label={`${p.name} (${p.layers?.length ?? 0})`}
            color={p.id === activePolicyId ? "primary" : "default"}
            onClick={() => setEditingId(p.id)}
            onDelete={() => dispatch(pricingActions.deletePolicy(p.id))}
            size={"small"}
          />
        ))}
        {policies.length > 0 && (
          <Select
            size={"small"}
            displayEmpty
            value={activePolicyId ?? ""}
            onChange={(e) =>
              dispatch(pricingActions.setActivePolicy(e.target.value || null))
            }
            sx={{ minWidth: 180 }}
          >
            <MenuItem value={""}>{"No policy active"}</MenuItem>
            {policies.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        )}
        <Button color={"primary"} onClick={() => setCreateOpen(true)}>
          {"Create Policy"}
        </Button>
      </Stack>

      <PolicyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        layers={layers}
        initialPolicy={null}
        existingPolicies={policies}
        onSave={({ name, layers: layerIds }) =>
          dispatch(pricingActions.createPolicy({ name, layers: layerIds }))
        }
      />
      <PolicyDialog
        open={editingPolicy !== null}
        onClose={() => setEditingId(null)}
        layers={layers}
        initialPolicy={editingPolicy}
        existingPolicies={policies}
        onSave={({ name, layers: layerIds }) =>
          dispatch(
            pricingActions.updatePolicy({
              id: editingPolicy.id,
              name,
              layers: layerIds,
            })
          )
        }
      />
    </>
  );
};

export default PolicyManager;
